import { Flex, Modal } from 'antd'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Customer } from '~app/graph/generated/graphql'
import AccountSection from './AccountSection'
import ActionsSection from './ActionsSection'
import CaptureSettingsSection from './CaptureSettingsSection'
import IdentitySection from './IdentitySection'
import OriginalsAndReturnsSection from './OriginalsAndReturnsSection'
import ReportingSection from './ReportingSection'

/**
 * The sections that commit on their own Save, in the order they appear on
 * screen — the close prompt lists them that way.
 */
const SAVABLE_SECTIONS = {
  originalsAndReturns: 'customerDetails.sections.originalsAndReturns',
  captureSettings: 'customerDetails.sections.captureSettings',
  reporting: 'customerDetails.sections.reporting',
} as const

type SavableSection = keyof typeof SAVABLE_SECTIONS

interface SectionState {
  isDirty: boolean
  isSaving: boolean
}

/*
 * Component
 */

interface Props {
  customer: Customer
  visible: boolean
  close: () => void
}

const CustomerDetailsModal = ({ visible, customer, close }: Props) => {
  const { t } = useTranslation()
  const [sectionStates, setSectionStates] = useState<
    Partial<Record<SavableSection, SectionState>>
  >({})

  // Stable per section, so reporting state does not re-run the reporting
  // effect inside every section on every render.
  const stateReporters = useMemo(
    () =>
      Object.fromEntries(
        (Object.keys(SAVABLE_SECTIONS) as SavableSection[]).map(section => [
          section,
          (state: SectionState) =>
            setSectionStates(current =>
              current[section]?.isDirty === state.isDirty &&
              current[section]?.isSaving === state.isSaving
                ? current
                : { ...current, [section]: state }
            ),
        ])
      ) as Record<SavableSection, (state: SectionState) => void>,
    []
  )

  // Read inside the confirm dialog's callbacks, which close over the render
  // that opened them - a save can settle while the dialog is still up.
  const sectionStatesRef = useRef(sectionStates)
  sectionStatesRef.current = sectionStates

  /**
   * What closing right now would cost, read fresh rather than from the render
   * that opened a dialog. A section that is saving is still dirty until the
   * write lands, but its edit is not the kind closing discards - it is already
   * on its way to the server.
   */
  const getCloseHazards = () => {
    const states = sectionStatesRef.current
    const sections = Object.keys(SAVABLE_SECTIONS) as SavableSection[]
    return {
      saving: sections.filter(section => states[section]?.isSaving),
      unsaved: sections.filter(
        section => states[section]?.isDirty && !states[section]?.isSaving
      ),
    }
  }

  const getSectionNames = ({ sections }: { sections: SavableSection[] }) =>
    sections.map(section => t(SAVABLE_SECTIONS[section])).join(', ')

  /**
   * Without autosave, closing discards whatever is unsaved, so it asks first
   * and names what is at stake. Both hazards can apply at once, and each is
   * stated on its own terms: an in-flight write cannot be cancelled by
   * closing, while an untouched edit is simply lost.
   */
  const closeWithGuard = () => {
    const { saving, unsaved } = getCloseHazards()

    if (saving.length === 0 && unsaved.length === 0) {
      close()
      return
    }

    const isSaving = saving.length > 0

    Modal.confirm({
      autoFocusButton: 'cancel',
      cancelButtonProps: { type: 'primary' },
      cancelText: isSaving
        ? t('customerDetails.confirm.closeWhileSaving.cancelText')
        : t('labels.no'),
      content: (
        <div>
          {isSaving && (
            <p>
              {t('customerDetails.confirm.closeWhileSaving.body', {
                sections: getSectionNames({ sections: saving }),
              })}
            </p>
          )}
          {unsaved.length > 0 && (
            <p>
              {t('customerDetails.confirm.close.body', {
                sections: getSectionNames({ sections: unsaved }),
              })}
            </p>
          )}
        </div>
      ),
      okButtonProps: { danger: true, type: 'primary' },
      okText: isSaving
        ? t('customerDetails.confirm.closeWhileSaving.okText')
        : t('customerDetails.confirm.close.okText'),
      onOk: () => {
        // A save can fail while this dialog is open, turning that section back
        // into an edit the copy above never warned about. Ask again rather
        // than drop it on a promise that is no longer true.
        const latest = getCloseHazards()
        if (latest.unsaved.some(section => !unsaved.includes(section))) {
          closeWithGuard()
          return
        }
        close()
      },
      title: isSaving
        ? t('customerDetails.confirm.closeWhileSaving.title')
        : t('customerDetails.confirm.close.title'),
    })
  }

  return (
    <Modal
      footer={null}
      open={visible}
      onCancel={closeWithGuard}
      closable
      width={800}
      title={t('misc.customerDetails')}
    >
      <Flex vertical gap={16}>
        <IdentitySection customer={customer} />

        {/* keys are namespaced per section: selecting another customer has to
            remount each one to reseed its baseline, and sibling keys must
            still be unique */}
        <OriginalsAndReturnsSection
          key={`returns-${customer.id}`}
          customer={customer}
          onStateChange={stateReporters.originalsAndReturns}
        />

        <CaptureSettingsSection
          key={`capture-${customer.id}`}
          customer={customer}
          onStateChange={stateReporters.captureSettings}
        />

        <ReportingSection
          key={`reporting-${customer.id}`}
          customer={customer}
          onStateChange={stateReporters.reporting}
        />

        <ActionsSection customerId={customer.id} />

        <AccountSection customer={customer} />
      </Flex>
    </Modal>
  )
}

export default CustomerDetailsModal
